<template>
  <a-dropdown
    :trigger="['click']"
    :visible="open"
    placement="bottomLeft"
    overlay-class-name="freight-vehicle-select__overlay"
    @visibleChange="onVisibleChange"
  >
    <div
      class="freight-vehicle-select__trigger"
      :class="{ 'freight-vehicle-select__trigger--open': open }"
      role="button"
      tabindex="0"
      :aria-expanded="open ? 'true' : 'false'"
      @keydown.enter.prevent="onVisibleChange(!open)"
      @keydown.space.prevent="onVisibleChange(!open)"
    >
      <span
        v-if="!selected.length"
        class="freight-vehicle-select__placeholder"
        >{{ placeholder }}</span
      >
      <div v-else class="freight-vehicle-select__tags">
        <a-tag
          v-for="tag in visibleTags"
          :key="tag.value"
          class="freight-vehicle-select__tag"
        >
          <span class="freight-vehicle-select__tag-label" :title="tag.label">
            {{ tag.label }}
          </span>
          <span
            class="freight-vehicle-select__tag-close"
            role="button"
            :aria-label="`Remover ${tag.label}`"
            @click.stop="remove(tag.value)"
            @mousedown.stop
          >
            <a-icon type="close" />
          </span>
        </a-tag>

        <a-tag
          v-if="hiddenCount"
          class="freight-vehicle-select__tag freight-vehicle-select__tag--more"
          :title="hiddenLabels"
        >
          +{{ hiddenCount }}
        </a-tag>
      </div>
      <a-icon type="down" class="freight-vehicle-select__arrow" />
    </div>

    <div slot="overlay" class="freight-vehicle-select__panel">
      <div class="freight-vehicle-select__search">
        <a-input
          ref="search"
          :value="search"
          placeholder="Digite o nome do veículo"
          @change="onSearchChange"
        >
          <a-icon slot="prefix" type="search" />
        </a-input>
      </div>

      <div class="freight-vehicle-select__list">
        <label
          v-for="option in visibleOptions"
          :key="option.value"
          class="freight-vehicle-select__option"
        >
          <a-checkbox
            :checked="isChecked(option.value)"
            @change="() => toggle(option.value)"
          />
          <span class="freight-vehicle-select__option-label">
            {{ option.label }}
          </span>
        </label>

        <p v-if="!visibleOptions.length" class="freight-vehicle-select__empty">
          {{ options.length ? "Nenhum veículo encontrado" : "Carregando veículos…" }}
        </p>
      </div>
    </div>
  </a-dropdown>
</template>

<script>
export default {
  name: "FreightTableVehicleSelect",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Selecione o veículo",
    },
    maxTags: {
      type: Number,
      default: 2,
    },
  },
  data() {
    return {
      open: false,
      search: "",
    };
  },
  computed: {
    selected() {
      return this.value || [];
    },
    visibleOptions() {
      const termo = String(this.search || "")
        .trim()
        .toLowerCase();
      if (!termo) return this.options;

      return this.options.filter(
        (option) => String(option.label || "").toLowerCase().indexOf(termo) !== -1,
      );
    },
    // Marcado que ainda não tem rótulo é veículo cujo domínio não carregou:
    // mostra o id em vez de sumir com a tag, senão o operador perde a seleção
    // de vista sem entender por quê.
    selectedTags() {
      return this.selected.map((id) => {
        const option = this.options.filter((item) => item.value === id)[0];
        return { value: id, label: option ? option.label : String(id) };
      });
    },
    // O campo ocupa um terço da linha: as tags além do limite viram "+N".
    visibleTags() {
      return this.selectedTags.slice(0, this.maxTags);
    },
    hiddenTags() {
      return this.selectedTags.slice(this.maxTags);
    },
    hiddenCount() {
      return this.hiddenTags.length;
    },
    hiddenLabels() {
      return this.hiddenTags.map((tag) => tag.label).join(", ");
    },
  },
  methods: {
    onVisibleChange(open) {
      this.open = open;
      if (!open) {
        this.search = "";
        return;
      }

      this.$nextTick(this.focusSearch);
    },
    /**
     * preventScroll é o ponto: o overlay do a-dropdown é montado no fim do body
     * e só recebe posição no frame seguinte, então focar antes disso rola a
     * página até o topo do documento. O focus() do a-input não repassa opções,
     * daí o input nativo.
     */
    focusSearch() {
      if (!this.open || !this.$refs.search) return;

      const input = this.$refs.search.$el.querySelector("input");
      if (input) input.focus({ preventScroll: true });
    },
    isChecked(id) {
      return this.selected.indexOf(id) !== -1;
    },
    onSearchChange(e) {
      this.search = e && e.target ? e.target.value : e;
    },
    // O painel continua aberto a cada marcação: escolher vários veículos é o
    // caso normal aqui, e fechar a cada clique obrigaria a reabrir e rebuscar.
    remove(id) {
      this.$emit(
        "change",
        this.selected.filter((item) => item !== id),
      );
    },
    toggle(id) {
      const next = this.isChecked(id)
        ? this.selected.filter((item) => item !== id)
        : this.selected.concat([id]);

      this.$emit("change", next);
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-vehicle-select {
  &__trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 11px;
    border: 1px solid @color-border-input;
    border-radius: 4px;
    background: @color-bg-surface;
    cursor: pointer;

    &:hover,
    &--open {
      border-color: @color-primary;
    }
  }

  &__placeholder {
    flex: 1;
    color: @color-text-muted;
    font-size: 14px;
  }

  &__tags {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: 4px;
    overflow: hidden;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    max-width: 100%;
    margin: 0;
    padding: 0 4px 0 8px;
    height: 24px;
    border-radius: 4px;
    background: @color-bg-subtle;
    border-color: @color-border-muted;
    color: @color-text-dark;
    font-size: 13px;

    &--more {
      flex: none;
      padding: 0 8px;
      color: @color-text-medium;
    }
  }

  &__tag-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__tag-close {
    display: inline-flex;
    align-items: center;
    padding: 2px;
    color: @color-text-light;
    cursor: pointer;
    font-size: 10px;

    &:hover {
      color: @color-text-dark;
    }
  }

  &__arrow {
    color: @color-text-light;
    font-size: 12px;
  }
}
</style>

<style lang="less">
@import "~@/shared/styles/variables.less";

// a-dropdown faz portal: o atributo de escopo não alcança o painel.
.freight-vehicle-select__overlay {
  .freight-vehicle-select__panel {
    min-width: 320px;
    padding: 8px 0;
    border-radius: 6px;
    background: @color-bg-surface;
    box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12),
      0 6px 16px 0 rgba(0, 0, 0, 0.08);
  }

  .freight-vehicle-select__search {
    padding: 4px 12px 8px;
  }

  .freight-vehicle-select__list {
    max-height: 220px;
    overflow-y: auto;
  }

  .freight-vehicle-select__option {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    padding: 8px 12px;
    cursor: pointer;
    font-weight: 400;

    &:hover {
      background: @color-bg-subtle;
    }
  }

  .freight-vehicle-select__option-label {
    font-size: 14px;
    color: @color-text-dark;
  }

  .freight-vehicle-select__empty {
    margin: 0;
    padding: 12px;
    font-size: 13px;
    color: @color-text-light;
    text-align: center;
  }
}
</style>
